import type { ReactNode } from "react";
import { useIntl } from "react-intl";
import { AbstractTile } from "@/components/design/AbstractTile";
import { HeroRevealImage } from "@/components/dashboard/StatTile";
import {
  creatorName,
  fiveStarRounded,
  fmtCount,
  itemTypeLabel,
} from "@/lib/share-item";
import type { ShareItemLike } from "@/lib/share-item";

/**
 * The byline for a card: whoever published it. For a company's post that is the COMPANY, and only
 * the company (@adr SPARK-0042-company-post-detail-shows-company-and-operator@2).
 *
 * It used to read `Company · Author`, on the reasoning that the company answers for the content
 * while the human who wrote it stays visible. At card size that second name never distinguished
 * anything — the company name alone already tells a company post from a personal one at a glance,
 * which is the byline's whole job here — and a list has no room to weigh two names against each
 * other. The operator is named on the DETAIL page instead, to every reader who can see the post
 * (concept v10, 0002 §6; @adr SPARK-0042-company-post-detail-shows-company-and-operator@2 records the reversal of the admin-only rule). Leaving it off
 * the card is about the card's density, not about hiding it.
 *
 * A PERSONAL post is unchanged: its author IS its publisher, so there is no second name to weigh.
 */
function byline(item: ShareItemLike, hideCompany = false): string {
  // `hideCompany` MEANS "the company is already known", which is only ever true on that company's
  // own page — so every tile under it is the company's, and there is nothing left for a byline to
  // add. Checked FIRST, and not as a branch of `item.company`: CompanyOwnedSharedItems does not
  // select `company` at all (the page supplied the id), so keying off the field would have fallen
  // through to the author and printed the operator's name on exactly the page @adr SPARK-0042-company-post-detail-shows-company-and-operator@2 is about.
  if (hideCompany) return "";
  if (item?.company?.name) return item.company.name;
  return creatorName(item.createdBy) || "–";
}

// Shared building blocks for SHARE item displays (card, cover, icons).
// Used by the SHARE overview, Discover feed, and detail views so cards look
// identical wherever they appear. Ported node-by-node from workshop's
// workshop/ui/ShareItemCard.jsx - engineering swaps are TS types + the pure
// helpers moved to lib/share-item.ts (kept separate so Fast Refresh works
// when this file is edited).

interface FileLike {
  mimeType?: string | null;
  content?: string | null;
}

// mainImage of the item as image, otherwise generic AbstractTile fallback
// (without "COVER" label).
export function ShareCover({
  item,
  index = 0,
  className = "",
}: {
  item: ShareItemLike;
  index?: number;
  className?: string;
}) {
  const img: FileLike | null = item?.mainImage ?? null;
  if (img?.content) {
    return (
      <img
        src={`data:${img.mimeType || "image/jpeg"};base64,${img.content}`}
        alt={item?.name || ""}
        className={`w-full object-cover ${className}`}
      />
    );
  }
  const variants = ["grid", "lines", "rings", "dots"] as const;
  const hues = [25, 200, 30, 280];
  return (
    <AbstractTile
      variant={variants[index % 4]!}
      hue={hues[index % 4]!}
      className={className}
    />
  );
}

// Unified item card for grids/feeds. `showActivity` reveals the per-user
// interaction chips (commented/watched/interested/rated).
export function ShareItemCard({
  item,
  idPrefix,
  index = 0,
  showActivity = false,
  hideCompany = false,
  heroImageSrc,
}: {
  item: ShareItemLike;
  idPrefix?: string;
  index?: number;
  showActivity?: boolean;
  /** Set on a company's own page, where naming the company on every tile says nothing. */
  hideCompany?: boolean;
  // Home-page hover polish: reveals section artwork ONLY behind the lower text area, not the
  // cover photo above — that already carries the item's own image.
  heroImageSrc?: string;
}) {
  return (
    <div id={idPrefix} className="box group flex h-full flex-col overflow-hidden bg-surface">
      <div className="relative">
        <ShareCover item={item} index={index} className="aspect-[4/3]" />
        <span className="absolute left-2 top-2 rounded bg-accent px-1.5 py-0.5 text-smycles-micro uppercase tracking-widest text-ink">
          {itemTypeLabel(item.itemType)}
        </span>
      </div>
      {/* main's hero-reveal structure, with this branch's byline inside it: the two changes are to
          different things — the reveal is about what sits BEHIND the lower text area, the byline is
          about what that text says — so the merge keeps both rather than choosing. */}
      <div className="relative flex flex-1 flex-col px-3 py-3">
        {heroImageSrc ? <HeroRevealImage src={heroImageSrc} /> : null}
        {/* relative: same stacking fix as StatTile — an absolutely positioned reveal sibling
            would otherwise paint above this static text regardless of DOM order. */}
        <div className="relative flex flex-1 flex-col">
          <div id={idPrefix ? `${idPrefix}-name` : undefined} className="text-smycles-body font-medium leading-tight line-clamp-1">{item.name}</div>
          <p className="mt-1 text-smycles-label leading-snug text-ink-muted line-clamp-2">{item.shortDescription}</p>
          <div className="mt-auto pt-2">
            {/* Nothing at all rather than an empty line: on a company's own page the byline has
                nothing left to say, and a blank row still takes its space. */}
            {byline(item, hideCompany) ? (
              <div data-byline className="text-smycles-micro text-ink-muted">
                {byline(item, hideCompany)}
              </div>
            ) : null}
            {showActivity ? <ActivityChips item={item} /> : null}
            <CardStats item={item} />
          </div>
        </div>
      </div>
    </div>
  );
}

// Image-first variant for dense horizontal Share previews: the card shows only the cover image
// by default, and expands downward on hover to reveal title + short description + stats — same
// grid-rows 0fr→1fr mechanic as StatTile's `actions` (components/dashboard/StatTile.tsx). Title
// lives in that panel, not overlaid on the image (tried, reverted — sidesteps the
// legibility-over-arbitrary-artwork problem entirely instead of solving it: scrim and glow were
// both tried and reverted too). Collapsed height is the image alone, so a row of these stays
// uniform until the visitor is actually hovering one.
export function ShareItemCardCover({
  item,
  idPrefix,
  index = 0,
  heroImageSrc,
  badge,
  footer,
  imageFallback = "abstract",
}: {
  item: ShareItemLike;
  idPrefix?: string;
  index?: number;
  heroImageSrc?: string;
  badge?: ReactNode;
  footer?: ReactNode;
  imageFallback?: "abstract" | "details";
}) {
  const detailsInCover = !item.mainImage?.content && imageFallback === "details";
  const details = (
    <>
      <div className="text-smycles-body font-medium leading-tight line-clamp-2">{item.name}</div>
      {item.shortDescription ? (
        <p id={idPrefix ? `${idPrefix}-description` : undefined} className="mt-1 text-smycles-label leading-snug text-ink-muted line-clamp-2">{item.shortDescription}</p>
      ) : null}
      {footer ? <div className="mt-auto pt-2 text-smycles-meta text-ink-muted">{footer}</div> : <CardStats item={item} />}
    </>
  );
  return (
    <div id={idPrefix} className="box group flex flex-col overflow-hidden bg-surface">
      <div className="relative">
        {detailsInCover ? (
          <div className="flex aspect-[4/3] flex-col px-3 pb-3 pt-10">{details}</div>
        ) : (
          <ShareCover item={item} index={index} className="aspect-[4/3]" />
        )}
        <span className="absolute left-2 top-2 rounded bg-accent px-1.5 py-0.5 text-smycles-micro uppercase tracking-widest text-ink">
          {badge ?? itemTypeLabel(item.itemType)}
        </span>
      </div>
      {!detailsInCover ? (
        <div className="grid grid-rows-[0fr] opacity-0 transition-[grid-template-rows,opacity] duration-500 ease-out group-hover:grid-rows-[1fr] group-hover:opacity-100 group-focus-within:grid-rows-[1fr] group-focus-within:opacity-100">
          <div className="relative min-h-0 overflow-hidden">
            {heroImageSrc ? <HeroRevealImage src={heroImageSrc} /> : null}
            <div className="relative flex flex-col px-3 py-3">{details}</div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** Breathing placeholder for a loading ShareItemCardCover. Deliberately the cover alone, with no
 *  text bars: the real card keeps its title panel collapsed until hover, so a taller skeleton
 *  would make the row jump the moment the data lands. */
export function ShareItemCardSkeleton({ className = "" }: { className?: string }) {
  return (
    <div className={`box overflow-hidden bg-surface ${className}`} aria-hidden="true">
      <div className="smycles-skeleton aspect-[4/3]" />
    </div>
  );
}

// shareditem-card-feed - the card for the SHARE Discover feed. Fixed tile
// aspect (4:5), vertical 60% mainImage on top / 40% text below (title ·
// shortDescription · author + counters). 4 per row in the grid.
export function ShareItemCardFeed({ item, idPrefix, index = 0, badge, footer }: {
  item: ShareItemLike;
  idPrefix?: string;
  index?: number;
  badge?: ReactNode;
  footer?: ReactNode;
}) {
  const intl = useIntl();
  return (
    <article id={idPrefix} className="box flex aspect-[4/5] flex-col overflow-hidden bg-surface">
      <div className="relative shrink-0 basis-[60%]">
        <ShareCover item={item} index={index} className="absolute inset-0 h-full w-full" />
        <span className="absolute left-2 top-2 rounded bg-accent px-1.5 py-0.5 text-smycles-micro uppercase tracking-widest text-ink">
          {badge ?? itemTypeLabel(item.itemType)}
        </span>
      </div>
      <div className="flex min-h-0 basis-[40%] flex-col p-3">
        <div className="min-h-0">
          <div id={idPrefix ? `${idPrefix}-name` : undefined} className="text-smycles-body font-medium leading-tight line-clamp-2">{item.name}</div>
          {item.shortDescription ? (
            <p id={idPrefix ? `${idPrefix}-description` : undefined} className="mt-1 text-smycles-meta leading-snug text-ink-muted line-clamp-2">{item.shortDescription}</p>
          ) : null}
        </div>
        <div className="mt-auto flex items-center justify-between gap-2 pt-2 text-smycles-meta text-ink-muted">
          {footer ?? (
            <>
              <span data-byline className="min-w-0 truncate text-ink-muted">
                {byline(item)}
              </span>
              <span className="flex shrink-0 items-center gap-2">
                <span className="inline-flex items-center gap-0.5" title={intl.formatMessage({ id: "design.cardRating", defaultMessage: "Rating" })}>
                  <ShareIcon name="star" className="h-3 w-3 text-ink" />
                  {item.rating ? fiveStarRounded(item.rating) : "–"}
                </span>
                <span className="inline-flex items-center gap-0.5" title={intl.formatMessage({ id: "design.cardViews", defaultMessage: "Views" })}>
                  <ShareIcon name="eye" className="h-3 w-3" /> {fmtCount(item.views)}
                </span>
                <span className="inline-flex items-center gap-0.5" title={intl.formatMessage({ id: "design.cardComments", defaultMessage: "Comments" })}>
                  <ShareIcon name="comment" className="h-3 w-3" /> {fmtCount(item.comments)}
                </span>
              </span>
            </>
          )}
        </div>
      </div>
    </article>
  );
}

function ActivityChips({ item }: { item: ShareItemLike }) {
  const intl = useIntl();
  type Chip = { icon: ShareIconName; label: string };
  const chips: Chip[] = [];
  if (item.owner) chips.push({ icon: "star", label: intl.formatMessage({ id: "design.chipCreated", defaultMessage: "Created" }) });
  if (item.commented) chips.push({ icon: "comment", label: intl.formatMessage({ id: "design.chipCommented", defaultMessage: "Commented" }) });
  if (item.pinned) chips.push({ icon: "pin", label: intl.formatMessage({ id: "design.chipWatched", defaultMessage: "Watched" }) });
  if (item.signedUp) chips.push({ icon: "hand", label: intl.formatMessage({ id: "design.chipInterested", defaultMessage: "Interested" }) });
  if (item.rated) chips.push({ icon: "star", label: intl.formatMessage({ id: "design.chipRated", defaultMessage: "Rated" }) });
  if (chips.length === 0) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-1">
      {chips.map((c) => (
        <span
          key={c.label}
          className="inline-flex items-center gap-1 rounded-full bg-accent/10 px-2 py-0.5 text-smycles-micro tracking-wide text-ink-accent"
        >
          <ShareIcon name={c.icon} className="h-2.5 w-2.5" /> {c.label}
        </span>
      ))}
    </div>
  );
}

function CardStats({ item }: { item: ShareItemLike }) {
  const intl = useIntl();
  return (
    <div className="mt-2.5 flex items-center gap-3 border-t border-ink/5 pt-2.5 text-smycles-micro text-ink-muted">
      <span className="inline-flex items-center gap-1" title={intl.formatMessage({ id: "design.cardRating", defaultMessage: "Rating" })}>
        <ShareIcon name="star" className="h-3 w-3 text-ink" />
        {item.rating ? fiveStarRounded(item.rating) : "–"}
        {item.ratings ? <span className="text-ink-muted">({fmtCount(item.ratings)})</span> : null}
      </span>
      <span className="inline-flex items-center gap-1" title={intl.formatMessage({ id: "design.cardViews", defaultMessage: "Views" })}>
        <ShareIcon name="eye" className="h-3 w-3" /> {fmtCount(item.views)}
      </span>
      <span className="inline-flex items-center gap-1" title={intl.formatMessage({ id: "design.cardComments", defaultMessage: "Comments" })}>
        <ShareIcon name="comment" className="h-3 w-3" /> {fmtCount(item.comments)}
      </span>
    </div>
  );
}

export type ShareIconName =
  | "star"
  | "clock"
  | "users"
  | "eye"
  | "comment"
  | "pin"
  | "hand"
  | "pencil"
  | "trash"
  | "dots";

// Monochrome inline icons (currentColor) - match the reduced design language.
export function ShareIcon({
  name,
  className = "w-3 h-3",
  filled = false,
}: {
  name: ShareIconName;
  className?: string;
  // Fill the (otherwise outline) glyph with currentColor, used for active
  // states like pinned / has-chat.
  filled?: boolean;
}) {
  const stroke = {
    className,
    viewBox: "0 0 24 24",
    fill: filled ? "currentColor" : "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };
  switch (name) {
    case "star":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
          <path d="M12 2l2.9 6.3 6.9.7-5.2 4.6 1.5 6.8L12 17.8 5.9 20.4l1.5-6.8L2.2 9l6.9-.7L12 2z" />
        </svg>
      );
    case "clock":
      return (
        <svg {...stroke} aria-hidden>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v5l3 2" />
        </svg>
      );
    case "users":
      return (
        <svg {...stroke} aria-hidden>
          <path d="M16 19v-1a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v1" />
          <circle cx="9" cy="7" r="3" />
          <path d="M22 19v-1a4 4 0 0 0-3-3.9" />
          <path d="M16 3.1a4 4 0 0 1 0 7.8" />
        </svg>
      );
    case "eye":
      return (
        <svg {...stroke} aria-hidden>
          <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      );
    case "comment":
      return (
        <svg {...stroke} aria-hidden>
          <path d="M21 11.5a8.5 8.5 0 0 1-12.2 7.6L3 21l1.9-5.8A8.5 8.5 0 1 1 21 11.5z" />
        </svg>
      );
    case "pin":
      return (
        <svg {...stroke} aria-hidden>
          <line x1="12" y1="17" x2="12" y2="22" />
          <path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z" />
        </svg>
      );
    case "hand":
      return (
        <svg {...stroke} aria-hidden>
          <path d="M8 13V5a1.5 1.5 0 0 1 3 0v6" />
          <path d="M11 11V4a1.5 1.5 0 0 1 3 0v7" />
          <path d="M14 11.5V6a1.5 1.5 0 0 1 3 0v7a6 6 0 0 1-6 6h-1a5 5 0 0 1-3.5-1.5L4 14.5a1.5 1.5 0 0 1 2.1-2.1L8 14" />
        </svg>
      );
    case "pencil":
      return (
        <svg {...stroke} aria-hidden>
          <path d="M12 20h9" />
          <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
        </svg>
      );
    case "trash":
      return (
        <svg {...stroke} aria-hidden>
          <path d="M3 6h18" />
          <path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" />
          <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
        </svg>
      );
    case "dots":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
          <circle cx="12" cy="5" r="1.6" />
          <circle cx="12" cy="12" r="1.6" />
          <circle cx="12" cy="19" r="1.6" />
        </svg>
      );
    default:
      return null;
  }
}
